/**
 * @fileoverview Structured data and shared metadata constants for the landing page.
 */
import { serializeJsonLdScript } from 'src/shared/seo/serialize-json-ld';
import { landingAssets } from '../landing-assets';
import { landingContent } from '../landing-content';

/**
 * The primary Flex hostname — the single literal the landing page's metadata and the
 * shared-host sitemap entry (`src/pages/sitemap.xml.ts`) both build on.
 *
 * It lives here (this module is the shared-metadata home, and imports nothing from the
 * crawl-file routes) so the canonical URL can be derived from it without an import cycle.
 */
export const LANDING_HOST = 'flex.attendevents.com';
export const LANDING_CANONICAL_URL = `https://${LANDING_HOST}/`;
export const LANDING_TITLE = 'Flex | Customizable Ticketing Experiences';
export const LANDING_DESCRIPTION =
  'Flex powers customizable ticket plans, suites, and live event experiences for teams, venues ' +
  'and entertainment providers.';

type TJsonLdNode = { '@type': string } & Record<string, unknown>;

/** Builds the `@graph` document embedded in the page head. */
export function buildLandingJsonLd(): { '@context': string; '@graph': TJsonLdNode[] } {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'Attend',
        url: 'https://www.attend.tech/',
        logo: landingAssets.attendLogo,
      },
      {
        '@type': 'SoftwareApplication',
        name: 'Flex',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: LANDING_CANONICAL_URL,
        description: landingContent.hero.lede,
        publisher: { '@type': 'Organization', name: 'Attend' },
      },
    ],
  };
}

/**
 * Serializes the structured data for inline embedding.
 *
 * Delegates to the shared `serializeJsonLdScript` so the landing page and the public
 * discovery pages escape structured data exactly one way: `JSON.stringify` does not
 * HTML-escape, so a `<` in the interpolated marketing copy could terminate the surrounding
 * `<script>` element. Escaping `<` to its JSON unicode form keeps the payload
 * byte-identical as data while making it inert as markup.
 */
export function serializeLandingJsonLd(): string {
  return serializeJsonLdScript(buildLandingJsonLd());
}
